<script setup>
import { ref, inject } from 'vue';
import { useProductStore } from '@/stores/product';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';
import { useToast } from 'vue-toastification';

const { search, destroy, bulkDestroy } = useProductStore();
const { all } = storeToRefs(useProductStore());

import { useOptionsStore } from '@/stores/options';
const optionsStore = useOptionsStore();
const { productTypeOptions } = storeToRefs(optionsStore);

const params = ref({
    term: '',
    product_type_id: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchProducts = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const toast = useToast();

const clearSearch = () => {
    params.value.term = '';
    searchProducts();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchProducts();
};

const remove = async (product) => {
    const confirm = await modals.show('ConfirmationModal', {
        props: {
            title: trans('label.confirm_delete'),
            prompt: trans('label.are_you_sure_delete'),
        }
    });

    if (confirm) {
        await destroy({ hash: product.hash });
        searchProducts();
    }
};

const openModal = (modal, mode = 'Create', product = null) => {
    modals.show(modal, {
        props: product ? {
            product: product,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchProducts();
    });
};

// Bulk selection state
const selectedProducts = ref([]);
// Bulk action state
const bulkAction = ref('');

const handleBulkAction = async () => {
    if (bulkAction.value === 'delete') {
        if (selectedProducts.value.length === 0) {
            return;
        }
        const confirm = await modals.show('ConfirmationModal', {
            props: {
                title: trans('label.confirm_delete'),
                prompt: trans('label.are_you_sure_delete'),
            }
        });

        const deletedProductsIds = selectedProducts.value.map(product => product.id);
        if (confirm) {
            try {
                await bulkDestroy(deletedProductsIds);
                selectedProducts.value = [];
                bulkAction.value = '';
                searchProducts();
                toast.success(trans('actions.bulk_delete.success', { type: 'Product' }));
            } catch (e) {
                toast.error(e.response.data.error);
            }
        }
    }
};
</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.management_title',{ 'what' : 'Product' }) }}</template>
    <template #actions>
        <StyledButton @click="openModal('ProductsModal')">
            <FontAwesomeIcon icon="plus" class="mr-1" /> {{ $t('label.create') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchProducts"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />

        <StyledDropdown
            v-model="params.product_type_id"
            :options="productTypeOptions"
            name="product_type"
            :label="$t('label.product_type')"
            @click:clear="clearSearch"
            @input="searchProducts" />
        
    </div>
    <div v-if="selectedProducts.length > 0" class="flex items-center justify-start mb-4 gap-x-6">
        <div class="w-1/2 lg:w-1/4">
            <StyledDropdown
                v-model="bulkAction"
                clearable
                :label="$t('label.action')"
                :options="[
                    { value: 'delete', label: $t('label.delete') },
                ]"
                :placeholder="$t('label.select_action')" />
        </div>
        <StyledButton
            class="-mt-4"
            :disabled="!bulkAction" 
            @click="handleBulkAction">
            {{ $t('label.apply') }}
        </StyledButton>
        <span class="text-sm -mt-2">{{ selectedProducts.length }} {{ $t('label.selected') }}</span>
    </div>

    <DataTable
        v-if="all"
        v-model="selectedProducts"
        :show-select="true"
        :data="all"
        :headers="[
            { title: $t('label.id'), key: 'id', value: item => `${item.id}` },
            { title: $t('label.name'), key: 'name', value: item => `${item.name}` },
            { title: $t('label.type'), key: 'type.name', value: item => `${item.type.name}` },
            { title: $t('label.price_per_unit'), key: 'price_per_unit', value: item => `$${item.price_per_unit}` },
        ]"
        :actions="[
            {
                title: $t('label.Edit'),
                icon: 'mdi-pencil',
                action: (item) => openModal('ProductsModal', 'Edit', item),
            },
            {
                title: $t('label.Delete'),
                icon: 'mdi-delete',
                action: (item) => remove(item),
            },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
